import { RotateCcw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { HEX_COLOR } from '@/lib/coach-guide';

/** '#abc' / '#abcd' → '#aabbcc' / '#aabbccdd'; anything else unchanged. */
function expand(hex: string) {
  if (!HEX_COLOR.test(hex) || hex.length > 5) return hex.toLowerCase();
  return ('#' + hex.slice(1).split('').map((c) => c + c).join('')).toLowerCase();
}

// Checkerboard behind the swatch so transparent colours read as transparent.
const CHECKER = 'repeating-conic-gradient(#d4d4d8 0% 25%, #fafafa 0% 50%) 50% / 10px 10px';

/**
 * A colour picker plus hex field. The native picker only knows #rrggbb, so
 * picking keeps any alpha the value already had (e.g. '#ffffff16' stays 16).
 */
export function ColorField({
  label,
  hint,
  value,
  defaultValue,
  error,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  defaultValue?: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  const valid = HEX_COLOR.test(value);
  const full = valid ? expand(value) : '#000000';
  const alpha = full.length === 9 ? full.slice(7) : '';
  const isDefault = defaultValue != null && expand(value) === expand(defaultValue);

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <label className="text-sm font-medium">{label}</label>
        {defaultValue != null && !isDefault && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-xs text-muted-foreground"
            onClick={() => onChange(defaultValue)}
            title={`Reset to ${defaultValue}`}
          >
            <RotateCcw className="mr-1 h-3 w-3" /> Default
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="relative h-9 w-9 shrink-0 overflow-hidden rounded-md border" style={{ background: CHECKER }}>
          <span className="absolute inset-0" style={{ background: valid ? value : 'transparent' }} />
          <input
            type="color"
            aria-label={`${label} colour picker`}
            value={full.slice(0, 7)}
            onChange={(e) => onChange(e.target.value + alpha)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </span>
        <Input
          value={value}
          onChange={(e) => {
            const v = e.target.value.trim();
            onChange(v && !v.startsWith('#') ? `#${v}` : v);
          }}
          spellCheck={false}
          maxLength={9}
          className={`h-9 font-mono text-sm ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
          aria-invalid={!!error}
        />
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
